import { useState, useMemo, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import { COVERS } from '../utils/data';
import ImageFromStorage from './ImageFromStorage';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Select from 'react-select';
import countryList from 'react-select-country-list';
import { updateUser } from '../firebase/firebase';
import { useTranslation } from 'react-i18next';
import { getIfTaxIdIsRequired, validateTaxId } from '../utils/taxId';

const ClientInfoModal = ({ visible, onClose, clientInfo }) => {
  const countries = useMemo(() => countryList().getData(), []);

  const clientCountryCode = clientInfo?.shipping_address?.country_code;
  const clientCountry = countries.find(
    (country) => country?.value === clientCountryCode
  );

  const [country, setCountry] = useState(clientCountry);
  const [taxIdError, setTaxIdError] = useState(false);

  // `recipient_tax_id` is required for shipping addresses to Brazil, Chile, and Mexico.
  // This field holds the recipient's tax identification number (CPF/CNPJ for Brazil, RUT for Chile, and RFC for Mexico).
  const isTaxIdRequired = getIfTaxIdIsRequired(country?.value);

  const { t } = useTranslation();

  // Reset tax ID error when modal opens or client changes
  useEffect(() => {
    setTaxIdError(false);
  }, [visible, clientInfo]);

  const onCountryChange = (value) => {
    setCountry(value);
    setTaxIdError(false); // Clear tax ID error when country changes
  };

  const handleSubmit = async (event) => {
    const form = event.currentTarget;
    event.preventDefault();

    const isTaxIdValid = validateTaxId(
      event.target?.recipientTaxId?.value,
      country?.value
    );

    setTaxIdError(!isTaxIdValid && isTaxIdRequired);

    console.log('isTaxIdValid', isTaxIdValid);
    if (form.checkValidity() === false || !isTaxIdValid) {
      console.log('tax id is not valid');
      event.stopPropagation();
    } else {
      const data = {
        name: event.target.name.value,
        shipping_address: {
          apartment: event.target?.apartment?.value,
          city: event.target.city.value,
          country_code: country?.value,
          first_name: event.target.firstName.value,
          last_name: event.target.lastName.value,
          phone: event.target.phone.value,
          ...(isTaxIdRequired
            ? { recipient_tax_id: event.target.recipientTaxId.value }
            : {}),
          state_code: event.target.state.value,
          street_address: event.target.streetAddress.value,
          zip_code: event.target.zipCode.value,
        },
      };

      await updateUser({
        uid: clientInfo?.uid,
        data,
        email: clientInfo?.email,
      });

      onClose();
    }
  };
  return (
    <Modal show={visible} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton style={{ border: 'none' }}>
        <h3 style={{ marginTop: 16 }}>Client details</h3>
      </Modal.Header>
      <Modal.Body>
        <h5>Book info</h5>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            marginBottom: 12,
          }}
        >
          <div>
            <p>Cover</p>
            {clientInfo?.cover && (
              <img
                alt="cover"
                src={COVERS?.[clientInfo?.cover - 1]}
                style={{ height: 140 }}
              />
            )}
          </div>
          <div style={{ marginLeft: 40 }}>
            <p>Cover photo</p>
            {clientInfo?.cover_photo && (
              <ImageFromStorage link={clientInfo?.cover_photo} height={140} />
            )}
          </div>
        </div>
        <hr />
        <h5 style={{ marginTop: 32, marginBottom: 16 }}>Shipping address</h5>
        <Form noValidate onSubmit={handleSubmit}>
          <Row>
            <Form.Group as={Col} md="6" className="mb-3" controlId="name">
              <Form.Label>Name on the book</Form.Label>
              <Form.Control
                defaultValue={clientInfo?.name}
                autoComplete="name"
                type="text"
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group as={Col} md="6" className="mb-3" controlId="firstName">
              <Form.Label>{t('dashboardScreen.firstName')}</Form.Label>
              <Form.Control
                defaultValue={clientInfo?.shipping_address?.first_name}
                autoComplete="given-name"
                type="text"
              />
            </Form.Group>
            <Form.Group as={Col} md="6" className="mb-3" controlId="lastName">
              <Form.Label>{t('dashboardScreen.lastName')}</Form.Label>
              <Form.Control
                defaultValue={clientInfo?.shipping_address?.last_name}
                autoComplete="family-name"
                type="text"
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group as={Col} md="6" className="mb-3" controlId="country">
              <Form.Label>{t('common.country')}</Form.Label>
              <Select
                options={countries}
                value={country}
                onChange={onCountryChange}
                defaultInputValue={clientCountry?.label}
              />
            </Form.Group>
            <Form.Group as={Col} md="6" className="mb-3" controlId="phone">
              <Form.Label>{t('dashboardScreen.phoneNumber')}</Form.Label>
              <Form.Control
                defaultValue={clientInfo?.shipping_address?.phone}
                type="text"
                autoComplete="tel"
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group
              as={Col}
              md="6"
              className="mb-3"
              controlId="streetAddress"
            >
              <Form.Label>{t('dashboardScreen.streetAddress')}</Form.Label>
              <Form.Control
                defaultValue={clientInfo?.shipping_address?.street_address}
                type="text"
                maxLength={30}
                autoComplete="address-line1"
              />
            </Form.Group>
            <Form.Group as={Col} md="6" className="mb-3" controlId="apartment">
              <Form.Label>{t('dashboardScreen.aptOptional')}</Form.Label>
              <Form.Control
                defaultValue={clientInfo?.shipping_address?.apartment}
                type="text"
                autoComplete="address-line2"
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group as={Col} md="6" className="mb-3" controlId="city">
              <Form.Label>{t('common.city')}</Form.Label>
              <Form.Control
                defaultValue={clientInfo?.shipping_address?.city}
                type="text"
                autoComplete="address-level2"
              />
            </Form.Group>
            <Form.Group as={Col} md="3" className="mb-3" controlId="state">
              <Form.Label>{t('common.state')}</Form.Label>
              <Form.Control
                defaultValue={clientInfo?.shipping_address?.state_code}
                type="text"
                placeholder="e.g. NY"
                maxLength={3}
                autoComplete="address-level1"
              />
            </Form.Group>
            <Form.Group as={Col} md="3" className="mb-3" controlId="zipCode">
              <Form.Label>{t('dashboardScreen.zipCode')}</Form.Label>
              <Form.Control
                defaultValue={clientInfo?.shipping_address?.zip_code}
                type="text"
                autoComplete="postal-code"
              />
            </Form.Group>
          </Row>
          {isTaxIdRequired && (
            <Row>
              <Form.Group
                as={Col}
                md="6"
                className="mb-3"
                controlId="recipientTaxId"
              >
                <Form.Label>{t('dashboardScreen.recipientTaxId')}</Form.Label>
                <Form.Control
                  type="text"
                  required
                  defaultValue={clientInfo?.shipping_address?.recipient_tax_id}
                  isInvalid={taxIdError}
                  autoComplete="tax-id"
                />
                <Form.Control.Feedback type="invalid">
                  {t('dashboardScreen.provideRecipientTaxId')}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
          )}
          <Button variant="dark" className="button-update" type="submit">
            {t('common.save')}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ClientInfoModal;
